import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// import.meta.env.DEV — Vite's own build-mode flag, not derived from whether
// VITE_FIREBASE_API_KEY happens to look configured (see api/index.ts for the
// full reasoning; this file had the identical issue — a production build
// with a missing/misconfigured Firebase key would previously fall back to
// DevAuthProvider's fixed dev-account/any-password login instead of real
// Firebase auth). A real production build now always uses FirebaseAuthProvider.
const IS_DEV = import.meta.env.DEV;

interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'CONTRACTOR';
  locationId?: number;
  contractorId?: number; // contractor this login maps to (self-service)
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Dev-mode accounts for local testing
const DEV_ACCOUNTS: Record<string, User> = {
  'admin@blingbling.com.au': {
    id: 'dev-admin-001',
    email: 'admin@blingbling.com.au',
    name: 'Admin',
    role: 'ADMIN',
  },
  'doug@blingbling.com.au': {
    id: 'dev-doug',
    email: 'doug@blingbling.com.au',
    name: 'Doug',
    role: 'CONTRACTOR',
    contractorId: 7,
  },
  'exequiel@blingbling.com.au': {
    id: 'dev-exequiel',
    email: 'exequiel@blingbling.com.au',
    name: 'Exequiel',
    role: 'CONTRACTOR',
    contractorId: 8,
  },
  'uriah@blingbling.com.au': {
    id: 'dev-uriah',
    email: 'uriah@blingbling.com.au',
    name: 'Uriah',
    role: 'CONTRACTOR',
    contractorId: 9,
  },
};

function DevAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const saved = sessionStorage.getItem('dev-auth-user');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading] = useState(false);

  const signIn = async (email: string, password: string) => {
    const account = DEV_ACCOUNTS[email.toLowerCase()];
    if (!account) {
      throw new Error(
        'Unknown dev account. Use: admin@blingbling.com.au / doug@blingbling.com.au / exequiel@blingbling.com.au / uriah@blingbling.com.au',
      );
    }
    if (password.length < 1) {
      throw new Error('Enter any password');
    }
    sessionStorage.setItem('dev-auth-user', JSON.stringify(account));
    setUser(account);
  };

  const signOut = async () => {
    sessionStorage.removeItem('dev-auth-user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

function FirebaseAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const { initializeApp } = await import('firebase/app');
      const {
        getAuth,
        onAuthStateChanged,
        signInWithEmailAndPassword: fbSignIn,
        signOut: fbSignOut,
      } = await import('firebase/auth');

      const app = initializeApp({
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      });
      const auth = getAuth(app);

      // Store for signIn/signOut closures
      (window as any).__fbAuth = auth;
      (window as any).__fbSignIn = fbSignIn;
      (window as any).__fbSignOut = fbSignOut;

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          const token = await firebaseUser.getIdTokenResult();
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || firebaseUser.email || '',
            role: (token.claims.role as 'ADMIN' | 'CONTRACTOR') || 'CONTRACTOR',
            locationId: token.claims.locationId as number | undefined,
          });
        } else {
          setUser(null);
        }
        setLoading(false);
      });
    })();

    return () => unsubscribe?.();
  }, []);

  const signIn = async (email: string, password: string) => {
    const fbSignIn = (window as any).__fbSignIn;
    const auth = (window as any).__fbAuth;
    await fbSignIn(auth, email, password);
  };

  const signOut = async () => {
    const fbSignOut = (window as any).__fbSignOut;
    const auth = (window as any).__fbAuth;
    await fbSignOut(auth);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (IS_DEV) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }
  return <FirebaseAuthProvider>{children}</FirebaseAuthProvider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
